import { Box, Heading, Input, Select, useDisclosure } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import TagFilter from "../../../components/TagFilter";
import MultipleSelect from "../../../components/MultipleSelect";
import { Tag } from "../../../models/Filter";
import { useCompany } from "../../../contexts/CompanyContext";
import { TeamViewModel } from "../../../models/Team";
import { getTeams } from "../../../services/teamService";
import { processStatus } from "../../Configurations/tabs/types";

export const managementFilterTypes = [
  { label: "Nº do processo", type: "cvfRef" },
  { label: "NCM", type: "ncm" },
  { label: "Origem", type: "origin" },
  { label: "Fornecedor", type: "supplier" },
  { label: "Produto", type: "product" },
  { label: "Responsável", type: "responsible" },
];

// Mesmo conjunto de filtros da tela /processes, compartilhado entre painel e funil de Gestão.
export const useManagementFilters = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const auth = useAuthUser<any>();
  const { currentCompany, currentEmulation } = useCompany();
  const { isOpen: filtersOpen, onOpen: onOpenFilters, onClose: onCloseFilters } = useDisclosure();

  const [tags, setTags] = useState<Tag[]>([]);
  const [isUrgent, setIsUrgent] = useState<boolean>();
  const [status, setStatus] = useState<string[]>([]);
  const [importMode, setImportMode] = useState<string>();
  const [teamId, setTeamId] = useState<string>();
  const [teams, setTeams] = useState<TeamViewModel[]>([]);
  const [dateType, setDateType] = useState("openingDate");
  const [initialDate, setInitialDate] = useState<string>();
  const [finalDate, setFinalDate] = useState<string>();

  // Equipe é conceito da Distribuidora, mas usuário de empresa "Geral" enxerga tudo.
  const canFilterByTeam = currentCompany === 2 || auth?.company === "Geral";

  useEffect(() => {
    if (!canFilterByTeam) {
      setTeamId(undefined);
      return;
    }
    getTeams().then((response) => setTeams(response ?? []));
  }, [canFilterByTeam]);

  const tagValues = (type: string) =>
    tags
      .filter((t) => t.type === type)
      .map((t) => t.value)
      .join("|");

  const filterParams = {
    type: currentCompany,
    companyId: currentEmulation,
    isUrgent,
    multiStatus: status.join("|"),
    teamId,
    importMode,
    dateType,
    initialDate,
    finalDate,
    clientRef: tagValues("clientRef"),
    ncm: tagValues("ncm"),
    departurePort: tagValues("departurePort"),
    dischargePort: tagValues("dischargePort"),
    cvfRef: tagValues("cvfRef"),
    countryOfOrigin: tagValues("origin"),
    exporter: tagValues("supplier"),
    responsible: tagValues("responsible"),
    product: tagValues("product"),
    insurance: tagValues("insurance"),
  };

  const activeFilterChips = [
    ...tags.map((t) => ({
      key: `tag-${t.type}-${t.value}`,
      label: `${t.label.trim().replace(/:$/, "")}: ${t.value}`,
      onRemove: () =>
        setTags((prev) => prev.filter((x) => !(x.type === t.type && x.value === t.value))),
    })),
    ...(isUrgent !== undefined
      ? [
          {
            key: "isUrgent",
            label: isUrgent ? "Urgente" : "Não urgente",
            onRemove: () => setIsUrgent(undefined),
          },
        ]
      : []),
    ...status.map((s) => ({
      key: `status-${s}`,
      label: `Status: ${s}`,
      onRemove: () => setStatus((prev) => prev.filter((x) => x !== s)),
    })),
    ...(teamId
      ? [
          {
            key: "teamId",
            label: `Equipe: ${teams.find((t) => t.id === teamId)?.name ?? teamId}`,
            onRemove: () => setTeamId(undefined),
          },
        ]
      : []),
    ...(importMode
      ? [
          {
            key: "importMode",
            label: `Modo: ${importMode}`,
            onRemove: () => setImportMode(undefined),
          },
        ]
      : []),
    ...(initialDate && finalDate
      ? [
          {
            key: "period",
            label: `Período: ${initialDate} a ${finalDate}`,
            onRemove: () => {
              setInitialDate(undefined);
              setFinalDate(undefined);
            },
          },
        ]
      : []),
  ];

  // Uma única fonte de verdade: a contagem é sempre o número de chips exibidos.
  const activeFilterCount = activeFilterChips.length;

  const clearAllFilters = () => {
    setTags([]);
    setIsUrgent(undefined);
    setStatus([]);
    setTeamId(undefined);
    setImportMode(undefined);
    setInitialDate(undefined);
    setFinalDate(undefined);
  };

  const visibleExtraFilters = [
    <Box>
      <Heading size="sm" mb="8px">Urgência</Heading>
      <Select
        value={isUrgent === undefined ? "all" : isUrgent ? "urgent" : "notUrgent"}
        onChange={(event) =>
          setIsUrgent(
            event.target.value === "all" ? undefined : event.target.value === "urgent"
          )
        }
        backgroundColor="white"
        width={isMobile ? "80vw" : "16vw"}
      >
        <option value="all">Todos</option>
        <option value="urgent">Urgentes</option>
        <option value="notUrgent">Não urgentes</option>
      </Select>
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">Status</Heading>
      <MultipleSelect
        selectedValues={status}
        onChange={(e) => setStatus(e)}
        backgroundColor="white"
        width={isMobile ? "80vw" : "16vw"}
        // CANCELADO fica sempre fora da Gestão (funil/KPIs o ignoram) — não oferecer
        // como opção evita um filtro que sempre retorna vazio.
        options={processStatus.filter((p) => p.value !== "CANCELADO").map((p) => p.value)}
      />
    </Box>,
    <TagFilter {...{ tags, setTags }} type="clientRef" label="Ref. do cliente: " />,
  ];

  const extraFilters = [
    ...(canFilterByTeam
      ? [
          <Box>
            <Heading size="sm" mb="8px">Equipe</Heading>
            <Select
              value={teamId ?? ""}
              onChange={(e) => (!e.target.value ? setTeamId(undefined) : setTeamId(e.target.value))}
              backgroundColor="white"
              width={isMobile ? "80vw" : "16vw"}
            >
              <option value=""></option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Box>,
        ]
      : []),
    <TagFilter {...{ tags, setTags }} type="insurance" label="Seguro: " />,
    <Box>
      <Heading size="sm" mb="8px">Modo de importação</Heading>
      <Select
        value={importMode ?? ""}
        onChange={(e) => (!e.target.value ? setImportMode(undefined) : setImportMode(e.target.value))}
        backgroundColor="white"
        width={isMobile ? "80vw" : "16vw"}
      >
        <option value=""></option>
        <option>Encomenda</option>
        <option>Distribuição</option>
        <option>Conta e Ordem</option>
      </Select>
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">Tipo do período</Heading>
      <Select
        backgroundColor="white"
        width={isMobile ? "80vw" : "16vw"}
        value={dateType}
        onChange={(e) => setDateType(e.target.value)}
      >
        <option value="openingDate">Data de abertura</option>
        <option value="arrivalDate">Data de chegada</option>
      </Select>
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">Início do período</Heading>
      <Input
        backgroundColor="white"
        type="date"
        width={isMobile ? "80vw" : "16vw"}
        value={initialDate}
        onChange={(e) => setInitialDate(e.target.value)}
      />
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">Fim do período</Heading>
      <Input
        backgroundColor="white"
        type="date"
        width={isMobile ? "80vw" : "16vw"}
        value={finalDate}
        onChange={(e) => setFinalDate(e.target.value)}
      />
    </Box>,
  ];

  return {
    tags,
    setTags,
    filterParams,
    activeFilterCount,
    activeFilterChips,
    clearAllFilters,
    visibleExtraFilters,
    extraFilters,
    filtersOpen,
    onOpenFilters,
    onCloseFilters,
    // dependências primitivas pra useEffect de quem consome (tags/status/teams são objetos/arrays,
    // então o efeito de load() deve depender desses valores individualmente, não do objeto filterParams inteiro).
    deps: [tags, isUrgent, status, teamId, importMode, dateType, initialDate, finalDate, currentEmulation] as const,
  };
};
